/**
 * 에러 로깅 서비스
 * AIRocketReg 각 서비스에서 발생한 에러를 서버에 전송하여 DB에 저장합니다.
 */

import axios from 'axios';
import store from '@/store';

/**
 * 에러 타입 상수
 */
export const ERROR_TYPES = {
  S3_UPLOAD_FAILED: 'S3_UPLOAD_FAILED',
  AI_REQUEST_FAILED: 'AI_REQUEST_FAILED',
  IMAGE_PROCESS_FAILED: 'IMAGE_PROCESS_FAILED',
  BACKGROUND_REMOVAL_FAILED: 'BACKGROUND_REMOVAL_FAILED',
  TRANSLATION_FAILED: 'TRANSLATION_FAILED',
  ATTRIBUTE_GENERATION_FAILED: 'ATTRIBUTE_GENERATION_FAILED',
  COUPANG_DOC_UPDATE_FAILED: 'COUPANG_DOC_UPDATE_FAILED',
  KEYWORD_COLLECTION_FAILED: 'KEYWORD_COLLECTION_FAILED',
  PARSING_FAILED: 'PARSING_FAILED',
  SIZE_CHART_FAILED: 'SIZE_CHART_FAILED',
  KOREAN_LABEL_FAILED: 'KOREAN_LABEL_FAILED',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  PROCESSING_ERROR: 'PROCESSING_ERROR',
  QUOTATION_FAILED: 'QUOTATION_FAILED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN: 'UNKNOWN'
};

/**
 * 서비스 에러를 서버에 로깅
 * @param {string} service - 서비스명 (예: 'mainImageService', 'seoService')
 * @param {string} functionName - 함수명 (예: 'processImage', 'handleSeoResult')
 * @param {Error|string} error - 에러 객체 또는 에러 메시지
 * @param {Object} context - 추가 컨텍스트 정보
 * @param {string} context.errorType - 에러 타입 (ERROR_TYPES 상수 사용)
 * @param {string} context.fallbackAction - 폴백 동작 설명
 * @param {string} context.productId - 상품 ID
 * @param {string} context.optionNumber - 옵션 번호
 * @param {Object} context.additional - 추가 정보
 */
export async function logServiceError(service, functionName, error, context = {}) {
  try {
    // 에러 메시지 추출
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    // 현재 선택된 상품에서 productId 추출 (context에 없는 경우)
    let productId = context.productId;
    if (!productId) {
      const selectedProduct = store.state.selectedRocketProduct;
      productId = selectedProduct?.registrationId || null;
    }

    // 로그 데이터 구성
    const logData = {
      service,
      functionName,
      errorType: context.errorType || ERROR_TYPES.UNKNOWN,
      errorMessage,
      fallbackAction: context.fallbackAction || null,
      productId,
      optionNumber: context.optionNumber || null,
      additionalContext: context.additional || {}
    };

    // 서버에 전송 (비동기, 실패해도 무시)
    axios.post('/api/log/service-error', logData).catch(err => {
      // 로깅 실패는 콘솔에만 출력 (무한 루프 방지)
      console.warn('[ErrorLogging] 서버 전송 실패:', err.message);
    });

    // 로컬 콘솔에도 출력 (개발 시 확인용)
    console.log(`[ErrorLogging] ${service}.${functionName}: ${context.errorType || 'ERROR'} - ${errorMessage}`);
    if (context.fallbackAction) {
      console.log(`  └─ Fallback: ${context.fallbackAction}`);
    }
  } catch (loggingError) {
    // 로깅 자체 실패 시 콘솔에만 출력
    console.warn('[ErrorLogging] 로깅 중 오류:', loggingError.message);
  }
}

/**
 * S3 업로드 실패 로깅 헬퍼
 */
export function logS3UploadError(service, functionName, error, context = {}) {
  return logServiceError(service, functionName, error, {
    errorType: ERROR_TYPES.S3_UPLOAD_FAILED,
    fallbackAction: context.fallbackAction || '원본 URL 사용',
    ...context
  });
}

/**
 * AI 요청 실패 로깅 헬퍼
 */
export function logAIRequestError(service, functionName, error, context = {}) {
  return logServiceError(service, functionName, error, {
    errorType: ERROR_TYPES.AI_REQUEST_FAILED,
    ...context
  });
}

/**
 * 이미지 처리 실패 로깅 헬퍼
 */
export function logImageProcessError(service, functionName, error, context = {}) {
  return logServiceError(service, functionName, error, {
    errorType: ERROR_TYPES.IMAGE_PROCESS_FAILED,
    ...context
  });
}

/**
 * coupangDoc 업데이트 실패 로깅 헬퍼
 */
export function logCoupangDocUpdateError(service, functionName, error, context = {}) {
  return logServiceError(service, functionName, error, {
    errorType: ERROR_TYPES.COUPANG_DOC_UPDATE_FAILED,
    fallbackAction: context.fallbackAction || '메모리에만 적용됨',
    ...context
  });
}

export default {
  ERROR_TYPES,
  logServiceError,
  logS3UploadError,
  logAIRequestError,
  logImageProcessError,
  logCoupangDocUpdateError
};

